import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Refresh, WarningTriangleOutline } from 'iconoir-react';
import Dashboard from '../../../Dashboard';
import { Loader } from '../../../../../Loader/Loader';
import { showData } from '../../../../../service/Contrato/showData';
import { TrackingStepper } from './TrackingStepper';
import { CurrentStatus, RequestSummary } from './TrackingSummary';
import { TrackingHistory } from './TrackingHistory';
import { TrackingMap } from './TrackingMap';

const Message = ({ children, onRetry }) => (
    <div className="flex flex-col items-center gap-3 py-16 text-gray-500">
        <WarningTriangleOutline width={32} height={32} />
        <p className="text-center">{children}</p>
        {onRetry && (
            <button
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-white bg-colorprimary hover:bg-colorsecondary"
                onClick={onRetry}
            >
                <Refresh width={16} height={16} /> Reintentar
            </button>
        )}
    </div>
);

export const TrackingPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tracking, setTracking] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error

    const loadTracking = useCallback(() => {
        const token = window.sessionStorage.getItem('token');
        setStatus('loading');
        showData('GET', token, `/portal/${id}/tracking/`)
            .then((res) => {
                if (res?.status !== 200 || !res?.data) throw new Error(res?.msg);
                setTracking(res.data);
                setStatus('ready');
            })
            .catch(() => setStatus('error'));
    }, [id]);

    useEffect(() => {
        loadTracking();
    }, [loadTracking]);

    const { capabilities = {}, meta = {} } = tracking || {};

    return (
        <Dashboard Msolicitudes="active">
            <section className="mx-auto py-6 px-4 w-full max-w-6xl flex flex-col gap-4 text-left">
                <button
                    className="self-start inline-flex items-center gap-1.5 text-colorprimary hover:text-colorsecondary text-sm font-medium"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft width={16} height={16} /> Volver a Mis solicitudes
                </button>

                <h2 className="font-semibold text-colorfourth text-center">
                    Seguimiento de la solicitud
                </h2>

                {status === 'loading' && <Loader />}
                {status === 'error' && (
                    <Message onRetry={loadTracking}>No se pudo cargar el seguimiento de la solicitud.</Message>
                )}

                {status === 'ready' && (
                    <>
                        {/* SIGT no respondió: se sigue mostrando la información local de la solicitud */}
                        {meta.external_lookup_performed && meta.external_service_available === false && (
                            <div className="rounded-lg border border-orange-200 bg-orange-50 text-orange-700 text-sm px-3 py-2">
                                La información de la orden de trabajo no está disponible por el momento.
                            </div>
                        )}

                        <TrackingStepper etapas={tracking.etapas} />

                        <div className="grid gap-4 lg:grid-cols-2">
                            <div className="flex flex-col gap-4">
                                <CurrentStatus estado={tracking.estado_actual} asignacion={tracking.asignacion} />
                                <TrackingHistory historial={tracking.historial} />
                            </div>
                            <div className="flex flex-col gap-4">
                                {capabilities.show_map && (
                                    <TrackingMap
                                        coordinates={tracking.ubicacion?.coordinates}
                                        address={tracking.resumen?.direccion}
                                    />
                                )}
                                <RequestSummary resumen={tracking.resumen} ordenId={tracking.orden_id} />
                            </div>
                        </div>

                        {/* TODO: documentos y evidencias cuando capabilities.upload_evidence sea true */}
                    </>
                )}
            </section>
        </Dashboard>
    );
};
